import { useEffect, useState, useRef } from "react";
import { AdminShell } from "@/components/Layout";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardText, Plus, X, Trash, FileText, CheckCircle, Circle, Warning, Camera, Image as ImageIcon } from "@phosphor-icons/react";

const STATUS_BADGE = {
  not_started: "bg-slate-300 text-slate-900",
  in_progress: "bg-amber-500 text-white",
  ready: "bg-emerald-500 text-white",
};

export default function InspectionPrep() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/inspection-prep");
      setRows(r.data || []);
    } catch { toast.error("Failed to load inspection prep"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="inspect-prep-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <ClipboardText weight="fill" className="text-fuchsia-500"/> Inspection Prep Kit
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Philly L&amp;I checklist per property — smoke detectors, GFCI, lead cert, egress. Prep the folder the day before, hand the tech a print-ready PDF.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-inspect-btn"
            className="h-11 px-4 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
            <Plus size={14}/> Start Prep Kit
          </button>
        </div>

        <div className="rugged-card p-4">
          {busy ? <div className="text-sm text-slate-500 py-4">Loading…</div>
          : rows.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No prep kits yet. Start one before your next L&amp;I inspection.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rows.map(p => (
                <button key={p.id} onClick={() => setDetail(p.id)}
                  data-testid={`prep-card-${p.id}`}
                  className="p-3 bg-white border-2 border-slate-200 hover:border-fuchsia-500 text-left transition space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-sm truncate">{p.property_address}{p.unit && ` · ${p.unit}`}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {p.inspection_date || "no date set"} · {p.assigned_tech_name || "unassigned"}
                      </div>
                    </div>
                    <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${STATUS_BADGE[p.status]}`}>
                      {p.status.replace("_", " ")}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 overflow-hidden">
                    <div className={`h-full ${p.status === "ready" ? "bg-emerald-500" : "bg-fuchsia-500"}`}
                      style={{ width: `${p.pct}%` }}/>
                  </div>
                  <div className="text-[10px] text-slate-600 flex justify-between">
                    <span>{p.checked} / {p.total} checked</span>
                    {p.required_open > 0 && (
                      <span className="text-red-700 font-black">⚠ {p.required_open} required open</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {showNew && (
          <NewPrepModal onClose={() => setShowNew(false)}
            onSaved={(p) => { setShowNew(false); load(); setDetail(p.id); }}/>
        )}
        {detail && (
          <PrepDetail id={detail} onClose={() => { setDetail(null); load(); }}/>
        )}
      </div>
    </AdminShell>
  );
}

function NewPrepModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    property_address: "", unit: "", inspection_date: "", assigned_tech_id: "", notes: "",
  });
  const [workers, setWorkers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/workers").then(r => setWorkers(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/inspection-prep", form);
      toast.success("Prep kit created");
      onSaved(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="new-prep-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-fuchsia-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Start L&amp;I Prep Kit</div>
          <button onClick={onClose} data-testid="new-prep-close" className="p-1 hover:bg-fuchsia-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <input list="known-addresses" autoComplete="off" value={form.property_address} onChange={e => setForm({...form, property_address: e.target.value})}
            placeholder="Property address" data-testid="prep-address"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="Unit (optional)"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
            Inspection date
            <input type="date" value={form.inspection_date} onChange={e => setForm({...form, inspection_date: e.target.value})}
              data-testid="prep-date"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </label>
          <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
            Assign to tech
            <select value={form.assigned_tech_id} onChange={e => setForm({...form, assigned_tech_id: e.target.value})}
              data-testid="prep-tech"
              className="h-10 px-3 border-2 border-slate-300 text-sm">
              <option value="">— Unassigned —</option>
              {workers.map(w => <option key={w.id || w._id} value={w.id || w._id}>{w.name || w.email}</option>)}
            </select>
          </label>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
            placeholder="Any special notes for the tech"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !form.property_address}
            data-testid="prep-save-btn"
            className="w-full h-11 bg-fuchsia-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Creating…" : "Create Prep Kit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PrepDetail({ id, onClose }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/inspection-prep/${id}`); setP(r.data); }
    catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, [id]);

  const toggle = async (itemId, checked) => {
    try {
      const r = await api.patch(`/inspection-prep/${id}/items/${itemId}`, { checked });
      setP(r.data);
    } catch { toast.error("Update failed"); }
  };
  const setNotes = async (itemId, notes) => {
    try {
      const r = await api.patch(`/inspection-prep/${id}/items/${itemId}`, { notes });
      setP(r.data);
    } catch { toast.error("Update failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this prep kit?")) return;
    try { await api.delete(`/inspection-prep/${id}`); toast.success("Deleted"); onClose(); }
    catch { toast.error("Delete failed"); }
  };
  const download = async () => {
    try {
      const res = await api.get(`/inspection-prep/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `inspect-prep-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
  };

  if (!p) return null;
  const byCat = {};
  p.items.forEach(it => { (byCat[it.category] = byCat[it.category] || []).push(it); });

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[80] flex items-start justify-center p-4 overflow-y-auto" data-testid="prep-detail">
      <div className="bg-white w-full max-w-4xl border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] my-4">
        <div className="p-3 bg-fuchsia-600 text-white flex items-start justify-between sticky top-0 z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Prep Kit</div>
            <div className="font-black uppercase tracking-widest text-sm truncate">
              {p.property_address}{p.unit && ` · ${p.unit}`}
            </div>
            <div className="text-[10px] opacity-90 mt-0.5">
              {p.inspection_date || "no date"} · {p.assigned_tech_name || "unassigned"} · {p.checked}/{p.total} · {p.required_open > 0 ? `⚠ ${p.required_open} required open` : "✓ All required checked"}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button onClick={download} data-testid="prep-pdf-btn"
              className="h-9 px-3 bg-white text-fuchsia-700 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
              <FileText size={12}/> PDF
            </button>
            <button onClick={del} data-testid="prep-delete"
              className="h-9 w-9 bg-red-600 hover:bg-red-700 inline-flex items-center justify-center">
              <Trash size={14}/>
            </button>
            <button onClick={onClose} data-testid="prep-close" className="p-1 hover:bg-fuchsia-700"><X size={20}/></button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          <div className="h-2 bg-slate-200 overflow-hidden">
            <div className={`h-full ${p.status === "ready" ? "bg-emerald-500" : "bg-fuchsia-500"}`}
              style={{ width: `${p.pct}%` }}/>
          </div>

          {Object.entries(byCat).map(([cat, items]) => (
            <div key={cat} className="rugged-card p-3">
              <h3 className="font-black uppercase tracking-widest text-[11px] text-slate-700 mb-2 border-b-2 border-slate-900 pb-1">{cat}</h3>
              <div className="space-y-1">
                {items.map(it => (
                  <ItemRow key={it.id} it={it} pid={id} onToggle={toggle} onNotes={setNotes} onReload={load}/>
                ))}
              </div>
            </div>
          ))}

          {p.notes && (
            <div className="p-3 bg-amber-50 border-l-4 border-amber-500 text-sm">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">Notes for Tech</div>
              <div className="mt-1">{p.notes}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemRow({ it, pid, onToggle, onNotes, onReload }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/inspection-prep/${pid}/items/${it.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo attached");
      onReload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
    finally { setBusy(false); }
  };

  const removePhoto = async (url) => {
    try {
      await api.delete(`/inspection-prep/${pid}/items/${it.id}/photo?photo_url=${encodeURIComponent(url)}`);
      onReload();
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div className="grid grid-cols-12 gap-2 items-start py-1" data-testid={`item-${it.id}`}>
      <button onClick={() => onToggle(it.id, !it.checked)}
        data-testid={`toggle-${it.id}`}
        className="col-span-1 h-8 w-8 inline-flex items-center justify-center shrink-0">
        {it.checked
          ? <CheckCircle weight="fill" size={22} className="text-emerald-600"/>
          : <Circle size={22} className={it.required ? "text-slate-400" : "text-slate-300"}/>}
      </button>
      <div className="col-span-5 text-sm">
        <div className={it.checked ? "line-through text-slate-500" : "font-medium"}>
          {it.label}
        </div>
        {it.required && !it.checked && (
          <span className="text-[9px] font-black uppercase tracking-widest text-red-700">Required</span>
        )}
        {(it.photo_urls || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {(it.photo_urls || []).map((u, i) => (
              <div key={u} className="relative group">
                <a href={fileUrl(u)} target="_blank" rel="noopener noreferrer">
                  <img src={fileUrl(u)} alt="" className="h-14 w-14 object-cover border-2 border-emerald-500"
                    data-testid={`photo-thumb-${it.id}-${i}`}/>
                </a>
                <button onClick={() => removePhoto(u)}
                  data-testid={`photo-del-${it.id}-${i}`}
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 bg-red-600 text-white rounded-full text-[9px] font-black opacity-0 group-hover:opacity-100">×</button>
              </div>
            ))}
          </div>
        )}
      </div>
      <input defaultValue={it.notes}
        onBlur={e => e.target.value !== it.notes && onNotes(it.id, e.target.value)}
        placeholder="Notes"
        data-testid={`notes-${it.id}`}
        className="col-span-4 h-8 px-2 border-2 border-slate-200 text-xs"/>
      <div className="col-span-2 flex justify-end">
        <input ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
          data-testid={`photo-input-${it.id}`}
          className="hidden"/>
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          data-testid={`photo-btn-${it.id}`}
          className="h-8 px-2 border-2 border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-700 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 disabled:opacity-50">
          <Camera size={12}/> {busy ? "…" : "Snap"}
        </button>
      </div>
    </div>
  );
}

